/**
 * The course's sessions in the person's own Google Calendar (ADR 0040): one
 * row, one click. IMAM owns the course calendar in the school's Workspace and
 * shares it read-only with the person's school account, so every session,
 * move and cancellation arrives on its own. Nothing is copied, pasted or
 * renewed, and leaving the course takes the calendar away.
 *
 * Where the school has a browser client, the press asks Google for a one-time
 * calendar-list token, shares the calendar, and adds it to the person's list
 * itself (see `google-calendar-subscribe.ts`). Otherwise it opens a tab at once
 * (so the browser counts the click), shares the calendar, then sends that tab
 * to Google's own "Add calendar" page for the school account. A 404 means the
 * course offers no calendar to this seat: the row isn't drawn. Keyed by
 * course, so an answer never crosses courses.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Bento } from "@/components/ui/box";
import type { FactBox, Read } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { googleCalendarApi, type CourseGoogleCalendar } from "@/lib/api/googleCalendar";
import { preloadGoogleCalendarSubscribe, subscribeCourseCalendars } from "@/lib/google-calendar-subscribe";
import { refusedRead } from "./course-page-boxes";
import { GOOGLE_CALENDAR_COPY, subscribeProblem } from "./google-calendar-copy";

const key = (courseId: string) => ["google-calendar", courseId] as const;

export function CourseGoogleCalendarFrame({ courseId }: { courseId: string }) {
  return <GoogleCalendarRow key={courseId} courseId={courseId} />;
}

function GoogleCalendarRow({ courseId }: { courseId: string }) {
  const { box, problem, note } = useGoogleCalendarBox(courseId);
  if (box.read.status === "forbidden") return null;
  // What the press did or couldn't do is said beside the row, once, never as a banner.
  return <div className="grid gap-1">
    <Bento id="course-google-calendar" journey="course-calendar" boxes={[box]} />
    {problem && <p role="alert" className="text-ui">{problem}</p>}
    {!problem && note && <p role="status" className="text-ui">{note}</p>}
  </div>;
}

function useGoogleCalendarBox(courseId: string): {
  box: FactBox<CourseGoogleCalendar>; problem: string | null; note: string | null;
} {
  const copy = GOOGLE_CALENDAR_COPY;
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: key(courseId), queryFn: () => googleCalendarApi.get(courseId), retry: false, staleTime: 0 });
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const subscribe = status.data?.state === "available" ? status.data.subscribe : null;
  useEffect(() => { if (subscribe) preloadGoogleCalendarSubscribe(); }, [subscribe]);

  const readAt = new Date(status.dataUpdatedAt).toISOString();
  // A refusal withdraws the row, even after an earlier read (R1); a fault keeps the last answer, marked stale (R2).
  const read: Read<CourseGoogleCalendar> = refusedRead(status.error)
    ? { status: "forbidden" }
    : status.data
      ? status.isError
        ? { status: "stale", last: status.data, readAt, sentence: copy.stale, refresh: () => void status.refetch() }
        : { status: "ready", value: status.data, readAt }
      : status.isError
        ? { status: "failed", sentence: copy.failed, retry: () => void status.refetch() }
        : { status: "loading" };

  const subscribeHere = async () => {
    if (!subscribe) return;
    setProblem(null);
    setNote(null);
    let refused = false;
    try {
      const outcome = await subscribeCourseCalendars(subscribe, async () => {
        const answer = await googleCalendarApi.add(courseId);
        queryClient.setQueryData(key(courseId), answer);
        return answer.calendar_id
          ? { ready: [{ course_id: courseId, calendar_id: answer.calendar_id }], pending: [] }
          : { ready: [], pending: [courseId] };
      });
      if (outcome.kind === "added" && outcome.added > 0) setNote(copy.addedHere);
      else if (outcome.kind === "added") setProblem(copy.notAdded);
      else setProblem(subscribeProblem(outcome, copy.notAdded));
    } catch (error) {
      refused = refusedRead(error);
      if (!refused) setProblem(error instanceof ApiError && error.message ? error.message : copy.notAdded);
    }
    if (refused) await queryClient.invalidateQueries({ queryKey: key(courseId) });
  };

  const add = async () => {
    setProblem(null);
    // Claimed within the press, before the request, so the tab isn't blocked.
    const tab = window.open("about:blank", "_blank");
    if (!tab) { setProblem(copy.blocked); return; }
    tab.opener = null;
    try {
      const answer = await googleCalendarApi.add(courseId);
      queryClient.setQueryData(key(courseId), answer);
      if (answer.link) tab.location.replace(answer.link);
      else tab.close();
    } catch (error) {
      tab.close();
      if (refusedRead(error)) await queryClient.invalidateQueries({ queryKey: key(courseId) });
      else setProblem(error instanceof ApiError && error.message ? error.message : copy.notAdded);
    }
  };

  const value = read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
  const box: FactBox<CourseGoogleCalendar> = {
    kind: "fact",
    id: `google-calendar-${courseId}`,
    label: copy.label,
    job: copy.job,
    owner: { system: "imam" },
    fold: "never",
    read,
    prose: (v) => (v.state === "added" ? copy.added : v.state === "available" ? copy.available : copy.noAccount),
    detail: (v) => (v.state === "no_account" ? "" : copy.detail),
    act: value?.state === "added" && value.link
      ? { does: "leave", verb: copy.open, providerUrl: value.link }
      : value?.state === "available"
        ? { does: "leave", verb: copy.add, run: value.subscribe ? subscribeHere : add }
        : undefined,
  };
  return { box, problem, note };
}
